import { useEffect } from "react";

/**
 * useSeo — inject/update per-route document.title, meta description,
 * Open Graph, Twitter Card and (optional) JSON-LD structured data on
 * the fly. Used by public marketing pages AND per-lesson pages so
 * every crawlable route ships full SEO signals even inside a client-
 * rendered SPA.
 *
 * Args:
 *   title       — <title> and og:title / twitter:title
 *   description — meta description + og:description + twitter:description
 *   ogImage     — absolute or same-origin URL (auto-resolved via `url`)
 *   url         — canonical URL for og:url
 *   jsonLd      — optional plain object; rendered inline as
 *                 <script type="application/ld+json"> and torn down on
 *                 unmount (Google supports arrays too — pass one).
 */
export default function useSeo({ title, description, ogImage, url, jsonLd }) {
  useEffect(() => {
    if (!title && !description) return undefined;
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    if (ogImage) {
      const abs = url ? new URL(ogImage, url).href : ogImage;
      setMeta("og:image", abs, "property");
      setMeta("twitter:image", abs);
    }
    if (url) setMeta("og:url", url, "property");
    setMeta("og:type", "article", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    let script = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-mwq-seo", "1");
      try { script.text = JSON.stringify(jsonLd); }
      catch (_) { script = null; }
      if (script) document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, ogImage, url, jsonLd]);
}
